import type { StrictSpacingStyle } from "../util/types";

import { PageHeader } from "../PageHeader";
import { useTheme } from "../ThemeProvider";

import { type ReactElement, type Ref, useMemo } from "react";
import { type Text, StyleSheet, View } from "react-native";

export interface BottomSheetHeaderProps {
  /**
   * Title text to display in the center of the header
   */
  title: string;

  /**
   * Optional custom element to render in place of the title text
   */
  titleImage?: ReactElement;

  /**
   * the content to the right of the title
   */
  headerRight?: ReactElement;

  /**
   * the content to the left of the title
   * Note: this is only used for interior variant as the root variant has the title on the left
   */
  headerLeft?: ReactElement;

  /**
   * Whether the header should have a border at the bottom
   */
  showBorder?: boolean;

  /**
   * Test ID for the header container
   */
  testID?: string;

  /**
   * Content to display below the header row but above the border
   */
  contentBelow?: ReactElement;

  /**
   * Whether to show the grabber handle at the top
   */
  showGrabber?: boolean;

  /**
   * Style overrides for the container
   */
  sx?: StrictSpacingStyle;

  /**
   * Ref forwarded to the title Heading element
   */
  titleRef?: Ref<Text>;

  /**
   * Hides the title from assistive technology (e.g. when the title is
   * already announced elsewhere, such as a dialog's accessible name).
   */
  accessibleHiddenTitle?: boolean;
}

export function BottomSheetHeader({
  title,
  titleImage,
  headerRight,
  headerLeft,
  showBorder = false,
  testID,
  contentBelow,
  showGrabber = true,
  sx,
  titleRef,
  accessibleHiddenTitle = false,
}: BottomSheetHeaderProps): ReactElement {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flexDirection: "column",
          paddingTop: 10,
          backgroundColor: theme.colors.background,
        },
        grabberContainer: {
          alignItems: "center",
        },
        grabber: {
          width: 36,
          height: 4,
          borderRadius: 2,
          backgroundColor: theme.colors.foregroundStrong,
        },
      }),
    [theme],
  );

  return (
    <View style={[styles.container, sx]}>
      {showGrabber && (
        <View style={styles.grabberContainer}>
          <View style={styles.grabber} testID="grabber" />
        </View>
      )}
      <PageHeader
        title={title}
        titleImage={titleImage}
        headerRight={headerRight}
        headerLeft={headerLeft}
        showBorder={showBorder}
        testID={testID}
        contentBelow={contentBelow}
        titleRef={titleRef}
        accessibleHiddenTitle={accessibleHiddenTitle}
      />
    </View>
  );
}
